import { Allocation, SortOption, Suggestion } from '@/app/types'
import { sendGAEvent } from '@next/third-parties/google'
import { omit } from 'lodash'

export const enum GA_EVENT {
	TEST_EVENT = 'test_event', //needed for testing GA events
	FORM_SEARCH_SUBMIT = 'search_submission',
	SMALL_SEARCH_CLICK = 'small_search_field_click',
	HOTEL_PAGE_REDIRECT_CLICK = 'hotel_page_click',
	HOTEL_CARD_REDIRECT_CLICK = 'hotel_card_click',
	HOTEL_ADDITIONAL_PRICE_CLICK = 'hotel_card_additional_price_click',
	SEARCH_FIELD_CLICK = 'search_field_click',
	SEARCH_ITEM_CLICK = 'search_field_location_selection',
	DATE_SELECT = 'form_date_select',
	ROOMS_CLICK = 'click_rooms_box',
	GUEST_OPTION_SELECT = 'form_guests_option_select',
	ADD_ROOM_CLICK = 'click_add_room',
	ADD_ROOM_CONFIRM = 'click_confirm_add_room',
	SIGN_UP_SWITCH_CLICK = 'click_sign_up_switch',
	EMAIL_FIELD_CLICK = 'click_enter_email',
	FORM_EMAIL_SUBSCRIBE = 'form_email_subscribe',
	// SIGN_UP_SEARCH_CLICK = 'click_sign_up_search',
	FORM_EMAIL_SUBSCRIBE_BOUNCE = 'email_subscribe_bounce',
	FORM_CALENDAR_CLICK = 'calendar_input_click',
	FORM_CALENDAR_BOUNCE = 'calendar_bounce',
	FORM_SUBMIT_ERROR = 'form_submit_error',
	FORM_NO_SUGGESTIONS_FOUND = 'no_suggestions_found',
	FORM_GUEST_SELECT = 'form_guests_select',
	LANGUAGE_SELECT_CLICK = 'language_selector_click',
	CURRENCY_SELECT_CLICK = 'currency_selector_click',
	LANGUAGE_CHANGE_CLICK = 'language_change_click',
	CURRENCY_CHANGE_CLICK = 'currency_change_click',
	FILTER_BUTTON_CLICK = 'filter_button_click',
	FILTER_OPTION_CLICK = 'filter_option_click',
	FILTER_RESET_CLICK = 'filter_reset_click',
	FILTER_APPLY_CLICK = 'filter_apply_click',
	FILTER_CLOSE_CLICK = 'filter_close_click',
	SORTING_BUTTON_CLICK = 'sorting_button_click',
	SORTING_OPTION_CLICK = 'sorting_option_click',
	SORTING_APPLY_CLICK = 'sorting_apply_click',
	SORTING_CLOSE_CLICK = 'sorting_close_click',
	EXPERIMENT_VIEWED = 'experiment_viewed',
	HOTEL_NO_AVAILABILITY = 'hotel_page_no_availability',
	POPULAR_DESTINATION_CLICK = 'popular_destination_click',
	SEARCH_HOTEL_NOW_CLICK = 'search_hotel_now_click',
	NO_RESULTS_FOUND = 'no_results_found',
	RESULT_PAGE_DIRECT_VIEW = 'result_page_direct_view',

	// NEW
	HOME_PAGE_VIEW = 'home_page_view',
	// SEARCH_SUBMISSION = "search_submission",
	LOCATION_CLICK = 'location_click',
	LOCATION_ITEMS_SELECT = 'location_item_select',
	DATE_CLICK = 'date_click',
	FROM_DATE = 'from_date',
	TO_DATE = 'to_date',
	GUESTS_CLICK = 'guests_click',
	GUESTS_SELECT = 'guests_select',
	SAVE_GUESTS_CLICK = 'save_guests_click',
	// ADD_ROOM_CLICK = "add_room_click",
	FIND_HOTELS_POPULAR_DEST = 'find_hotels_popular_dest',
	SEARCH_HOTELS_NOW = 'search_hotels_now',
	SIGN_UP_DEALS = 'sign_up_deals',
	CONTACT_US_CLICK = 'contact_us_click',
	CURRENCY_CLICK = 'currency_click',
	LANGUAGE_CLICK = 'language_click',
	TERM_OF_USE_CLICK = 'term_of _use_click',
	PRIVACY_POLICY_CLICK = 'privacy_policy_click',
	SEARCH_WITHOUT_LOCATION = 'search_without_location',

	// Errors
	RP_NO_HOTELS_FOUND = 'rp_no_hotels_found',
	RP_NO_HOTELS_FOUND_FILTERS = 'rp_no_hotels_filters',
	RP_NO_SPECIFIC_HOTEL = 'rp_no_specific_hotel',
	RP_NO_SPECIFIC_HOTEL_SEE_AVAILABILITY = 'rp_no_specific_hotel_see_availability',
	RP_RESET_ALL_FILTERS = 'rp_reset_all_filters',

	// Filters
	RP_FILTER_AMENITIES_SELECT = 'rp_filter_amenities_select',
	RP_FILTER_POPULAR_SELECT = 'rp_filter_popular_select',
	RP_FILTER_STAY_SELECT = 'rp_filter_stay_select',
	RP_FILTER_GUEST_RATING_SELECT = 'rp_filter_guest_rating_select',
	RP_FILTER_STARS_SELECT = 'rp_filter_stars_select',
	RP_FITLER_HOTEL_STYLE_SELECT = 'rp_filter_hotel_style_select',
	RP_FILTER_PRICE_SELECT = 'rp_filter_price_select',
	RP_FILTER_CLICK = 'rp_filter_click',

	RP_SORT_BY = 'rp_sort_by',
}

type GTMSearchEventData = {
	value: string
	roomsAndGuests: Allocation[]
	dateFrom: string
	dateTo: string
}

type GTMHotelClickEventData = {
	name: string
	location: string
	rating: number
	stars: string
	bestPrice: string
	additionalPricePartner?: string | null
}

export const isMainPage = () => {
	if (!window.location.search.includes('sessionID')) {
		return true
	}
}

export const emitGAEventBase = (eventName: string, data?: any) => {
	sendGAEvent('event', eventName, { ...data })
}

export const emitRPGAEventBase = (eventName: GA_EVENT, data?: any) => {
	if (isMainPage()) {
		emitGAEventBase(eventName, data)
	} else {
		emitGAEventBase(`rp_${eventName}`, data)
	}
}

// ✅
export const emitHotelPageClickGAEvent = (data: GTMHotelClickEventData) => {
	const { name, location, rating, stars, bestPrice, additionalPricePartner } =
		data

	const eventData = {
		hotel_name: name,
		hotel_location: location,
		hotel_rating: rating,
		hotel_stars: stars,
		best_price_partner: bestPrice,
		...(additionalPricePartner && {
			additional_price_partner: additionalPricePartner,
		}),
	}

	emitGAEventBase(GA_EVENT.HOTEL_PAGE_REDIRECT_CLICK, eventData)
}

// ✅
export const emitHotelCardClickGAEvent = (data: GTMHotelClickEventData) => {
	const { name, location, rating, stars, bestPrice, additionalPricePartner } =
		data

	const eventName = additionalPricePartner
		? GA_EVENT.HOTEL_ADDITIONAL_PRICE_CLICK
		: GA_EVENT.HOTEL_CARD_REDIRECT_CLICK

	const eventData = {
		hotel_name: name,
		hotel_location: location,
		hotel_rating: rating,
		hotel_stars: stars,
		best_price_partner: bestPrice,
		...(additionalPricePartner && {
			additional_price_partner: additionalPricePartner,
		}),
	}

	emitGAEventBase(eventName, eventData)
}

// ✅
export const emitFormSearchGAEvent = (data: GTMSearchEventData) => {
	const { value, roomsAndGuests, dateFrom, dateTo } = data

	const eventData = {
		search_value: value,
		check_in_date: dateFrom,
		check_out_date: dateTo,
		rooms_guests: `${roomsAndGuests.length} rooms, ${roomsAndGuests.reduce(
			(acc, item) => acc + item.adults + item.childAge.length,
			0
		)} guests`,
	}

	emitRPGAEventBase(GA_EVENT.FORM_SEARCH_SUBMIT, eventData)
}

export const emitSmallBookingClickGAEvent = () => {
	emitGAEventBase(GA_EVENT.SMALL_SEARCH_CLICK)
}

// ✅
// export const emitSignUpSwitchClickGAEvent = () => {
// 	emitGAEventBase(GA_EVENT.SIGN_UP_SWITCH_CLICK)
// }

// ✅
export const emitEmailFieldClickGAEvent = () => {
	emitGAEventBase(GA_EVENT.EMAIL_FIELD_CLICK)
}

// ✅
export const emitFormEmailSubmitGAEvent = () => {
	emitGAEventBase(GA_EVENT.FORM_EMAIL_SUBSCRIBE)
}
// // ✅
// export const emitFormSignupSearchGAEvent = () => {
// 	emitGAEventBase(GA_EVENT.SIGN_UP_SEARCH_CLICK)
// }

export const emitFormEmailSubscribeBounceGAEvent = () => {
	emitGAEventBase(GA_EVENT.FORM_EMAIL_SUBSCRIBE_BOUNCE)
}

// ✅
// export const emitSearchFieldClickGAEvent = () => {
// 	emitGAEventBase(GA_EVENT.SEARCH_FIELD_CLICK)
// }

// ✅
// export const emitSearchItemClickGAEvent = (
// 	page: PAGE_TYPE,
// 	suggestion: Suggestion
// ) => {
// 	emitGAEventBase(GA_EVENT.SEARCH_ITEM_CLICK, {
// 		page,
// 		suggestion: JSON.stringify(suggestion),
// 	})
// }

// // ✅
// export const emitDateSelectGAEvent = (input: string, date: Date) => {
// 	const formattedDate = format(date as Date, DATE_FORMAT_QUERY)
// 	emitGAEventBase(GA_EVENT.DATE_SELECT, { input, date: formattedDate })
// }

// ✅
// export const emitRoomsClickGAEvent = () => {
// 	emitGAEventBase(GA_EVENT.ROOMS_CLICK)
// }

// ✅
export const emitGuestOptionSelectGAEvent = (
	type: 'one' | 'two' | 'family'
) => {
	emitGAEventBase(GA_EVENT.GUEST_OPTION_SELECT, { selected_value: type })
}

// ✅
// export const emitAddRoomClickGAEvent = () => {
// 	emitGAEventBase(GA_EVENT.ADD_ROOM_CLICK)
// }

// ✅
export const emitAddRoomConfirmClickGAEvent = () => {
	emitGAEventBase(GA_EVENT.ADD_ROOM_CONFIRM)
}

// ✅
// export const emitSearchFieldFocusGAEvent = () => {
// 	emitGAEventBase(GA_EVENT.SEARCH_FIELD_CLICK)
// }

// ✅
// export const emitFormCalendarClickGAEvent = () => {
// 	emitGAEventBase(GA_EVENT.FORM_CALENDAR_CLICK)
// }

// ❌
// export const emitFormCalendarBounceGAEvent = () => {
// 	emitGAEventBase(GA_EVENT.FORM_CALENDAR_BOUNCE)
// }
// ✅
// export const emitFormSubmitErrorGAEvent = () => {
// 	emitGAEventBase(GA_EVENT.FORM_SUBMIT_ERROR)
// }
// ✅
// export const emitFormNoSuggestionsFoundGAEvent = (query: string) => {
// 	emitGAEventBase(GA_EVENT.FORM_NO_SUGGESTIONS_FOUND, { user_query: query })
// }
// ✅
// export const emitFormGuestSelectGAEvent = (
// 	field: 'adults' | 'children',
// 	amount: number
// ) => {
// 	emitGAEventBase(GA_EVENT.FORM_GUEST_SELECT, { field, amount })
// }
// ✅
// export const emitLanguageSelectClickGAEvent = () => {
// 	emitGAEventBase(GA_EVENT.LANGUAGE_SELECT_CLICK)
// }
// // ✅
// export const emitCurrencySelectClickGAEvent = () => {
// 	emitGAEventBase(GA_EVENT.CURRENCY_SELECT_CLICK)
// }
// ✅
// export const emitLanguageChangeClickGAEvent = (
// 	prevLang: string,
// 	newLang: string
// ) => {
// 	emitGAEventBase(GA_EVENT.LANGUAGE_CHANGE_CLICK, {
// 		previous_language: prevLang,
// 		new_language: newLang,
// 	})
// }
// ✅
// export const emitCurrencyChangeClickGAEvent = (
// 	prevCurrency: string,
// 	newCurrency: string
// ) => {
// 	emitGAEventBase(GA_EVENT.CURRENCY_CHANGE_CLICK, {
// 		previous_currency: prevCurrency,
// 		new_currency: newCurrency,
// 	})
// }

// ✅
export const emitFilterButtonClickGAEvent = () => {
	emitGAEventBase(GA_EVENT.FILTER_BUTTON_CLICK)
}

// ✅
// export const emitFilterOptionClickGAEvent = (
// 	name: string,
// 	values: number | number[]
// ) => {
// 	emitRPGAEventBase(GA_EVENT.FILTER_OPTION_CLICK, {
// 		filter_option: name,
// 		values,
// 	})
// }

// ✅
// export const emitFilterResetClickGAEvent = () => {
// 	emitGAEventBase(GA_EVENT.FILTER_RESET_CLICK)
// }

// export const emitFilterApplyClickGAEvent = () => {
// 	emitGAEventBase(GA_EVENT.FILTER_APPLY_CLICK)
// }

// export const emitFilterCloseClickGAEvent = () => {
// 	emitGAEventBase(GA_EVENT.FILTER_CLOSE_CLICK)
// }

// ✅
// export const emitSortingButtonClickGAEvent = () => {
// 	emitGAEventBase(GA_EVENT.SORTING_BUTTON_CLICK)
// }

// ✅
export const emitSortingOptionClickGAEvent = (sortingOption: SortOption) => {
	emitGAEventBase(GA_EVENT.RP_SORT_BY, {
		by: sortingOption.by,
		dir: sortingOption.dir,
	})
}

// ✅
// export const emitSortingApplyClickGAEvent = () => {
// 	emitGAEventBase(GA_EVENT.SORTING_APPLY_CLICK)
// }

// ✅
// export const emitSortingCloseClickGAEvent = () => {
// 	emitGAEventBase(GA_EVENT.SORTING_CLOSE_CLICK)
// }

// export const emitExperimentViewedGAEvent = (
// 	experimentId: string,
// 	variationId: string
// ) => {
// 	emitGAEventBase(GA_EVENT.EXPERIMENT_VIEWED, {
// 		experiment_id: experimentId,
// 		variation_id: variationId,
// 	})
// }

// ✅
// export const emitHotelNoAvailabilityGAEvent = () => {
// 	emitGAEventBase(GA_EVENT.HOTEL_NO_AVAILABILITY)
// }

// ✅
// export const emitPopularDestinationClickGAEvent = (name: string) => {
// 	emitGAEventBase(GA_EVENT.POPULAR_DESTINATION_CLICK, { name })
// }

// ✅
// export const emitSearchHotelNowClickGAEvent = () => {
// 	emitGAEventBase(GA_EVENT.SEARCH_HOTEL_NOW_CLICK)
// }

// ✅
// export const emitNoResultsFoundGAEvent = (
// 	searchQuery: DestPagesProps['searchParams']
// ) => {
// 	emitGAEventBase(GA_EVENT.NO_RESULTS_FOUND, {
// 		search_query: searchQuery,
// 	})
// }

// NEW
// ✅
export const emitHomePageViewGAEvent = () => {
	emitGAEventBase(GA_EVENT.HOME_PAGE_VIEW)
}

// ✅
export const emitLocationClickGAEvent = () => {
	emitRPGAEventBase(GA_EVENT.LOCATION_CLICK)
}

// ✅
export const emitLocationItemSelectGAEvent = (suggestion: Suggestion) => {
	emitRPGAEventBase(
		GA_EVENT.LOCATION_ITEMS_SELECT,
		omit(suggestion, ['image', 'id'])
	)
}

// ✅
export const emitDateClickGAEvent = () => {
	emitRPGAEventBase(GA_EVENT.DATE_CLICK)
}

// ✅
export const emitFromDateGAEvent = () => {
	emitRPGAEventBase(GA_EVENT.FROM_DATE)
}

// ✅
export const emitToDateGAEvent = () => {
	emitRPGAEventBase(GA_EVENT.TO_DATE)
}

// ✅
export const emitGuestsClickGAEvent = () => {
	emitRPGAEventBase(GA_EVENT.GUESTS_CLICK)
}

// ✅
export const emitSaveGuestsClickGAEvent = () => {
	emitRPGAEventBase(GA_EVENT.SAVE_GUESTS_CLICK)
}

// ✅
export const emitAddRoomClickGAEvent = () => {
	emitRPGAEventBase(GA_EVENT.ADD_ROOM_CLICK)
}

// ✅
export const emitFindHotelsPopularDestGAEvent = (city: string) => {
	emitGAEventBase(GA_EVENT.FIND_HOTELS_POPULAR_DEST, { city })
}

// ✅
export const emitContactUsClickGAEvent = () => {
	emitGAEventBase(GA_EVENT.CONTACT_US_CLICK)
}

// ✅
export const emitTermOfUseClickGAEvent = () => {
	emitGAEventBase(GA_EVENT.TERM_OF_USE_CLICK)
}

// ✅
export const emitPrivacyPolicyClickGAEvent = () => {
	emitGAEventBase(GA_EVENT.PRIVACY_POLICY_CLICK)
}

// ✅
export const emitSearchHotelsNowGAEvent = () => {
	emitGAEventBase(GA_EVENT.SEARCH_HOTELS_NOW)
}

// ✅
export const emitSignUpDealsGAEvent = () => {
	emitGAEventBase(GA_EVENT.SIGN_UP_DEALS)
}

// ✅
export const emitCurrencyClickGAEvent = (currency: string) => {
	emitRPGAEventBase(GA_EVENT.CURRENCY_CLICK, { currency })
}

// ✅
export const emitLanguageClickGAEvent = (language: string) => {
	emitRPGAEventBase(GA_EVENT.LANGUAGE_CLICK, { language })
}

// ✅
export const emitRpNoHotelsFoundGAEvent = (params?: any) => {
	emitGAEventBase(GA_EVENT.RP_NO_HOTELS_FOUND, { ...params })
}
// ✅
export const emitRpNoHotelsFoundFiltersGAEvent = (params?: any) => {
	emitGAEventBase(GA_EVENT.RP_NO_HOTELS_FOUND_FILTERS, { ...params })
}

// ✅
export const emitRpNoSpecificHotelGAEvent = (params: any) => {
	emitGAEventBase(GA_EVENT.RP_NO_SPECIFIC_HOTEL, { ...params })
}

// ✅
export const emitRpNoSpecificHotelSeeAvailabilityGAEvent = () => {
	emitGAEventBase(GA_EVENT.RP_NO_SPECIFIC_HOTEL_SEE_AVAILABILITY)
}

// ✅
export const emitRpResetAllFiltersGAEvent = () => {
	emitGAEventBase(GA_EVENT.RP_RESET_ALL_FILTERS)
}

export const emitRpFilterClickGAEvent = (title: string, name: string) => {
	emitGAEventBase(GA_EVENT.RP_FILTER_CLICK, { title, name })
}

// ✅
export const emitRpFilterAmenitiesSelectGAEvent = (
	amenityId: number,
	value: boolean,
	name: string
) => {
	if (value) {
		emitGAEventBase(GA_EVENT.RP_FILTER_AMENITIES_SELECT, {
			amenityId,
			value,
			name,
		})
		emitRpFilterClickGAEvent('amenities', name)
	}
}

// ✅
export const emitRpFilterStaySelectGAEvent = (name: string, value: boolean) => {
	if (value) {
		emitGAEventBase(GA_EVENT.RP_FILTER_STAY_SELECT, { name, value })
		emitRpFilterClickGAEvent('stay', name)
	}
}

// ✅
export const emitRpFilterPopularSelectGAEvent = (
	name: string,
	value: boolean
) => {
	if (value) {
		emitGAEventBase(GA_EVENT.RP_FILTER_POPULAR_SELECT, { name, value })
		emitRpFilterClickGAEvent('popular', name)
	}
}

// ✅
export const emitRpFilterGuestRatingSelectGAEvent = (
	name: string,
	value: boolean
) => {
	if (value) {
		emitGAEventBase(GA_EVENT.RP_FILTER_GUEST_RATING_SELECT, { name, value })
		emitRpFilterClickGAEvent('guestRating', name)
	}
}

// ✅
export const emitRpFilterStarsSelectGAEvent = (
	name: number,
	value: boolean
) => {
	if (value) {
		emitGAEventBase(GA_EVENT.RP_FILTER_STARS_SELECT, { name, value })
		emitRpFilterClickGAEvent('stars', `${name} Stars`)
	}
}

// ✅
export const emitRpFilterHotelStyleSelectGAEvent = (
	name: string,
	value: boolean
) => {
	if (value) {
		emitGAEventBase(GA_EVENT.RP_FITLER_HOTEL_STYLE_SELECT, { name, value })
		emitRpFilterClickGAEvent('hotelStyle', name)
	}
}

// ✅
export const emitRpFilterPriceSelectGAEvent = (
	value: [number, number],
	currency: string
) => {
	emitGAEventBase(GA_EVENT.RP_FILTER_PRICE_SELECT, {
		min: value[0],
		max: value[1],
		currency,
	})
}

// ✅
export const emitGuestsSelectGAEvent = (data: {
	rooms: number
	adults: number
	children: number
	total: number
}) => {
	emitRPGAEventBase(GA_EVENT.GUESTS_SELECT, { ...data })
}

export const emitSearchWithoutLocationGAEvent = () => {
	emitRPGAEventBase(GA_EVENT.SEARCH_WITHOUT_LOCATION)
}
export const emitResultsPageDirectViewGAEvent = () => {
	emitGAEventBase(GA_EVENT.RESULT_PAGE_DIRECT_VIEW)
}
